import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { projectAPI } from "../../services/api";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix for default markers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

const ProjectSection = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        // ดึงข้อมูลโครงการทั้งหมดที่มีพิกัด
        const response = await projectAPI.getLocations();

        if (response.data && response.data.success) {
          setProjects(response.data.data);
        } else {
          setError("ไม่สามารถโหลดข้อมูลได้");
        }
      } catch (error) {
        console.error("Failed to fetch projects:", error);
        setError("เกิดข้อผิดพลาดในการโหลดข้อมูล");
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  const createCustomIcon = (color) => {
    return L.divIcon({
      className: "custom-marker",
      html: `<i class="fas fa-map-marker-alt" style="color: ${color}; font-size: 30px; text-shadow: 2px 2px 4px rgba(0,0,0,0.3);"></i>`,
      iconSize: [30, 30],
      iconAnchor: [15, 30],
      popupAnchor: [0, -30],
    });
  };

  const formatBudget = (budget) => {
    return new Intl.NumberFormat("th-TH", {
      style: "currency",
      currency: "THB",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(budget || 0);
  };

  // คำนวณสถิติ
  const totalProjects = projects.length;
  const uniqueProvinces = new Set(projects.map((p) => p.province)).size;
  const uniqueDepartments = new Set(projects.map((p) => p.dpt_name)).size;

  if (loading) {
    return (
      <section className="py-16 bg-gray-50">
        <div className="container-custom">
          <div className="flex justify-center">
            <div className="spinner"></div>
          </div>
        </div>
      </section>
    );
  }

  if (error || projects.length === 0) {
    return null;
  }

  // คำนวณจุดศูนย์กลางจากค่าเฉลี่ยของพิกัดทั้งหมด
  const center =
    projects.length > 0
      ? [
          projects.reduce((sum, p) => sum + parseFloat(p.lat), 0) /
            projects.length,
          projects.reduce((sum, p) => sum + parseFloat(p.lng), 0) /
            projects.length,
        ]
      : [15.0903, 104.3336];

  return (
    <section className="relative py-16 overflow-hidden bg-gradient-to-br from-blue-50 to-indigo-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h1
            className="text-3xl md:text-4xl font-bold text-primary-500 relative inline-block pb-4"
            style={{
              borderBottom: "3px solid #FFBF07",
            }}
          >
            <i className="fas fa-map-marked-alt text-secondary-500 mr-2"></i>
            พื้นที่บริการวิชาการ
          </h1>
        </motion.div>

        {/* Quick Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8 mb-5"
        >
          <div className="bg-white rounded-lg p-4 text-center shadow-sm hover:shadow-md transition-shadow">
            <div className="text-2xl font-bold text-primary-500">
              {uniqueProvinces}
            </div>
            <div className="text-xs text-gray-600">จังหวัด</div>
          </div>
          <div className="bg-white rounded-lg p-4 text-center shadow-sm hover:shadow-md transition-shadow">
            <div className="text-2xl font-bold text-primary-500">
              {totalProjects}
            </div>
            <div className="text-xs text-gray-600">โครงการทั้งหมด</div>
          </div>

          <div className="bg-white rounded-lg p-4 text-center shadow-sm hover:shadow-md transition-shadow">
            <div className="text-2xl font-bold text-primary-500">
              {uniqueDepartments}
            </div>
            <div className="text-xs text-gray-600">หน่วยงาน</div>
          </div>
        </motion.div>

        {/* Map Container */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
          className="bg-white rounded-xl shadow-lg overflow-hidden relative"
        >
          {/* Project Count */}
          <div className="absolute top-4 right-4 z-10 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg border border-gray-100">
            <span className="text-sm font-sans font-medium text-gray-700">
              <i className="fas fa-map-marker-alt mr-2 text-primary-500"></i>
              พบ {totalProjects} โครงการ
            </span>
          </div>

          <MapContainer
            center={center}
            zoom={8}
            style={{ height: "500px", width: "100%" }}
            className="rounded-lg shadow-inner font-sans"
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            />

            {projects.map((project) => (
              <Marker
                key={project.project_id}
                position={[parseFloat(project.lat), parseFloat(project.lng)]}
                icon={createCustomIcon(project.dpt_color)}
                eventHandlers={{
                  click: () => setSelectedProject(project),
                }}
              >
                <Popup className="font-sans">
                  <div className="w-64 bg-white rounded-lg overflow-hidden font-sans">
                    {/* Header with color border */}
                    <div
                      className="h-2 w-full"
                      style={{ backgroundColor: project.dpt_color }}
                    ></div>

                    <div className="p-4">
                      {/* Title */}
                      <h3 className="text-base font-semibold text-gray-800 mb-3 line-clamp-2">
                        {project.project_name}
                      </h3>

                      {/* Department */}
                      <p
                        className="text-sm font-medium mb-2 flex items-center gap-1"
                        style={{ color: project.dpt_color }}
                      >
                        <i className="fas fa-building text-xs"></i>
                        {project.dpt_name}
                      </p>

                      {/* Project Leader */}
                      <p className="text-sm text-gray-600 mb-2 flex items-center gap-1">
                        <i className="fas fa-user text-primary-500 text-xs"></i>
                        {project.mem_fname} {project.mem_sname}
                      </p>

                      {/* Location */}
                      <p className="text-sm text-gray-600 mb-4 flex items-center gap-1">
                        <i className="fas fa-map-marker-alt text-primary-500 text-xs"></i>
                        {project.subdistrict} {project.district}{" "}
                        {project.province}
                      </p>

                      {/* View Detail Button */}
                      <Link
                        to={`/projects/${project.project_id}`}
                        className="inline-flex items-center justify-center w-full gap-2 text-white bg-secondary-500  px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors text-sm font-medium"
                      >
                        ดูรายละเอียด
                        <i className="fas fa-arrow-right text-xs"></i>
                      </Link>
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>

          {/* Legend / Quick Info */}
          <div className="p-4 bg-white border-t">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-600">
                  <i className="fas fa-info-circle text-primary-500 mr-1"></i>
                  คลิกที่หมุดเพื่อดูรายละเอียดโครงการ
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">โครงการทั้งหมด:</span>
                <span className="text-sm font-semibold text-primary-500">
                  {totalProjects} โครงการ
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProjectSection;
